// @flow
import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { Container, Checkbox, Grid, GridColumn, GridRow, Button, Icon } from 'semantic-ui-react';
import * as css from './AddSubscription.module.css';
import Complete from 'views/Complete';
import Confirm from 'views/Confirm';
import Header from 'views/Header';
import Error from 'views/Error';
import Subscription from 'components/Subscription';
import InfoAccordion from 'components/Accordion';
import Footer from 'components/Footer';
import Modal from 'components/Modal';
import Loading from 'components';
import api from 'api';
import ReactGA from 'react-ga';
import { useSelector } from 'react-redux';
import { CustomerTypes, ModalTypes } from '../../Constants';
import { infoLogger, errorLogger } from '../../helpers';
import { getTermsFor, getContentFor } from '../../helpers/contentHelpers';

type Props = {
  eid: string,
  msisdn: string,
  ssn: string,
  pricePlanName: string,
  primaryImsi: string,
  name: string,
  companionPriceplans: int,
  hasUncompletedOrder: boolean,
  orderId: string,
  displayPromotion: boolean,
  hasIncludedSubscription: boolean,
};

const AddSubscription = ({
  eid,
  msisdn,
  ssn,
  pricePlanName,
  primaryImsi,
  name,
  companionPriceplans,
  hasUncompletedOrder,
  orderId,
  displayPromotion,
  hasIncludedSubscription,
}: Props) => {
  const [isComplete, setComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [newOrderId, setNewOrderId] = useState('');
  const [consent, setConsent] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPromotion, setShowPromotion] = useState(displayPromotion);
  const [isConfirmed, setConfirmed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const loading = useSelector((state) => state.app.isLoading);
  const error = useSelector((state) => state.app.error);

  const customerType = useSelector((state) => state.app.customerType);

  const terms = getTermsFor(CustomerTypes.FUTUR);
  const allContentt = getContentFor(customerType);
  const pageContent = allContentt.page.add;
  const readdContent = allContentt.page.readd;

  const postData = async () => {
    const body = {
      Ssn: ssn,
      PrimaryMsisdn: msisdn,
      PrimaryImsi: primaryImsi,
      PrimaryPriceplanProductName: pricePlanName,
      SecondaryEid: eid,
      Vendor: window.__VENDOR__,
    };
    setIsLoading(true);
    try {
      // Post Order'
      const result = await api.rest('v1/orders', body, 'POST');
      if (result.error) {
        errorLogger({ name: 'client-post-order', log: `Error in post order ${result.error}` });
        setErrorMsg(result.error);
        setIsError(true);
      } else {
        infoLogger({ name: 'client-post-order', log: 'Post order returned without error.' });
        const customerBody = {
          Eid: eid,
          Msisdn: msisdn,
          HasUncompletedOrder: true,
          OrderId: result.orderId,
          CustomerType: customerType,
          Vendor: window.__VENDOR__,
        };
        await api.rest('v1/customerStates', customerBody, 'POST');
        setNewOrderId(result.orderId);
        setComplete(true);
      }
    } catch (error) {
      errorLogger({ name: 'client-post-order', log: `Post order or customerStates failed: ${error.message}` });
      setErrorMsg(error.message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const addSubscription = useCallback(() => {
    infoLogger({ name: 'client-add-subscription', log: 'Add subscription' });
    postData();
  }, [ssn, msisdn, pricePlanName, eid]);

  const completeSubscription = useCallback(() => {
    if (orderId) {
      infoLogger({ name: 'client-complete-subscription', log: 'Complete subscription' });
      setNewOrderId(orderId);
    }
    setComplete(true);
  }, []);

  const toggleModalTerms = useCallback(() => {
    setShowTerms(!showTerms);
  }, [showTerms]);

  const toggleModalPromotion = useCallback(() => {
    setShowPromotion(!showPromotion);
  }, [showPromotion]);

  const handleConsentCheckboxChange = useCallback(() => {
    setConsent(!consent);
  });

  const closeModalAcceptTerms = useCallback(() => {
    setShowTerms(false);
    setConsent(true);
  }, [setConsent]);

  useEffect(() => {
    if (error) {
      errorLogger({ name: 'client-add-subscription-error', log: `Error in AddSubscription: ${error.message}` });
      setErrorMsg(error.message);
      setIsError(true);
    }
    if (hasUncompletedOrder) {
      infoLogger({
        name: 'client-has-uncompleted-order',
        log: `Customer has uncompleted order`,
      });
      setConsent(true);
    }
    if (customerType === CustomerTypes.MELODY) {
      setConsent(true);
    }
    const bodyElement = document.body;
    const hasVerticalScrollbar = bodyElement.scrollHeight > bodyElement.clientHeight;
    hasVerticalScrollbar ? setIsScrolled(true) : setIsScrolled(false);

    if (isConfirmed) {
      ReactGA.pageview('/subscription');
    }
  }, [isConfirmed, error]);

  useEffect(() => {
    document.body.style.overflow = (showPromotion && isConfirmed) || (showTerms && isConfirmed) ? 'hidden' : 'visible';
  }, [showPromotion, showTerms, isConfirmed]);

  useEffect(() => {
    window.__PAGE__ = 'Add subscription';
    infoLogger({ name: 'client-page-view-add-subscription', log: 'Add subscription page shown' });
  }, []);

  if (isError) return <Error message={errorMsg} />;
  if (loading || isLoading) return <Loading />;

  if (!isConfirmed) return <Confirm onConfirm={setConfirmed} device={name} isScrolled={isScrolled} />;
  if (isComplete && !isLoading) return <Complete orderId={newOrderId} msisdn={msisdn} eId={eid} />;

  const formattedPricePlanName = pricePlanName?.replace('Mobil ', '');

  return (
    <>
      <Header />
      {showPromotion && !hasIncludedSubscription && !hasUncompletedOrder && (
        <Modal content={allContentt.page.promotion} handleClose={toggleModalPromotion} type={ModalTypes.PROMOTION} />
      )}
      <Container as="main">
        <h4 className={css.title}>{hasUncompletedOrder ? readdContent.title : pageContent.title}</h4>
        {hasIncludedSubscription ? (
          <Subscription
            amount={formattedPricePlanName || 'Obegränsad'}
            price={'0 kr/mån'}
            info={`Tilläggsabonnemang för klocka ingår kostnadsfritt i ${formattedPricePlanName || 'ditt abonnemang'}. Har du Familj ingår varsitt klockabonnemang. Låt din smartwatch vara uppkopplad även när mobilen lämnas hemma`}
            selected={true}
          />
        ) : (
          <Subscription
            amount={pageContent.subscription.amount}
            price={pageContent.subscription.price}
            info={pageContent.subscription.info}
            selected={true}
            showPrice={customerType !== CustomerTypes.MELODY}
          />
        )}
        {(hasUncompletedOrder || companionPriceplans === 1) && (
          <div className={hasUncompletedOrder ? css.infoSelection : css.warning}>
            <Grid>
              <GridRow columns={2} verticalAlign={'middle'}>
                <GridColumn width={1}>
                  <Icon className={css.error} name="warning" />
                </GridColumn>
                <GridColumn width={11}>
                  <p className={css.info}>
                    {hasUncompletedOrder ? readdContent.subscription.warning : pageContent.subscription.warning}
                  </p>
                </GridColumn>
              </GridRow>
            </Grid>
          </div>
        )}
        {!hasUncompletedOrder && (
          <Grid>
            <GridRow columns={1}>
              <GridColumn>
                <InfoAccordion data={pageContent.moreInfo.accordion} />
              </GridColumn>
            </GridRow>
          </Grid>
        )}
        <Footer isScrolled={isScrolled}>
          <Fragment>
            {customerType !== CustomerTypes.MELODY && (
              <>
                <Checkbox className={css.checkbox} checked={consent} onChange={handleConsentCheckboxChange} />
                <span className={css.label}>
                  {pageContent.terms.text}
                  <span onClick={toggleModalTerms} className={css.toggleLink}>
                    {pageContent.terms.linkText}
                  </span>
                </span>
                {showTerms && (
                  <Modal
                    content={terms}
                    handleAccept={closeModalAcceptTerms}
                    handleClose={toggleModalTerms}
                    type={ModalTypes.TERMS}
                  />
                )}
              </>
            )}

            <Button
              color="blue"
              primary
              fluid
              onClick={hasUncompletedOrder ? completeSubscription : addSubscription}
              disabled={!consent}
              id="addSubscription"
            >
              {hasUncompletedOrder ? readdContent.button : pageContent.button}
            </Button>
          </Fragment>
        </Footer>
      </Container>
    </>
  );
};

export default React.memo(AddSubscription);
